import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Gift, Wine, Clock, CheckCircle2, Sparkles, Phone, Upload, PenTool, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Editable sample holiday visuals — swap with the winery's own photos anytime.
const IMG = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/4c3562f6-adb8-49c0-a054-fe7a3f82cf4d/images/37da16c15bceebeea92c3e23187be567a4f6b55a5d7a35556940e61033d4e425.jpeg',
  bottle: 'https://static.prod-images.emergentagent.com/jobs/4c3562f6-adb8-49c0-a054-fe7a3f82cf4d/images/007daa5ed6b5a0cede90b2317e5fa0e036bbb0a42961eabef050a11d2a4f89d7.jpeg',
  business: 'https://static.prod-images.emergentagent.com/jobs/4c3562f6-adb8-49c0-a054-fe7a3f82cf4d/images/2fbb99abb3d7dc513d611d8170e67e221e9efe8fc493b6bc6086660ac3ec5aa6.jpeg',
};

const WINE = '#722F37';
const GOLD = '#FDB913';

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="px-4 py-2 rounded-full text-sm font-semibold border transition-colors"
    style={active ? { background: WINE, color: '#fff', borderColor: WINE } : { background: '#fff', color: WINE, borderColor: '#e7c3ca' }}>
    {children}
  </button>
);

const HolidayGifting = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', giftType: 'personal', offering: 'bottles',
    quantity: '15', occasion: '', preferredContact: 'Phone', personalizationNotes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Two ways to handle the label: upload your own design, or work with our designer.
  const [label, setLabel] = useState({ option: 'designer', photo: null, previewUrl: '' });

  const onLabelPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast({ title: 'Please choose a JPG, PNG or WebP image', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: 'Please choose an image under 15 MB', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    setLabel((l) => {
      if (l.previewUrl) URL.revokeObjectURL(l.previewUrl);
      return { ...l, photo: file, previewUrl: URL.createObjectURL(file) };
    });
    e.target.value = '';
  };

  const clearPhoto = () => setLabel((l) => {
    if (l.previewUrl) URL.revokeObjectURL(l.previewUrl);
    return { ...l, photo: null, previewUrl: '' };
  });

  useEffect(() => () => { if (label.previewUrl) URL.revokeObjectURL(label.previewUrl); }, [label.previewUrl]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const scrollToForm = () => document.getElementById('gift-form')?.scrollIntoView({ behavior: 'smooth' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast({ title: 'Please add your name and phone number', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('labelOption', label.option);
      if (label.option === 'upload' && label.photo) fd.append('labelPhoto', label.photo);
      await axios.post(`${API}/contest/gift-request`, fd);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const Deadline = ({ className = '' }) => (
    <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${className}`}
      style={{ background: '#FFFBEA', color: '#8a5a00', border: `1px solid ${GOLD}` }} data-testid="gift-deadline">
      <Clock className="w-4 h-4" /> Order by Nov 15 (Nov 17 for 5-week wines) for Christmas
    </div>
  );

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#fdfbf5' }}>
        <div className="max-w-md text-center bg-white rounded-2xl shadow-sm p-8" data-testid="gift-confirmation">
          <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: WINE }} />
          <h1 className="text-2xl font-bold mb-2" style={{ color: WINE, fontFamily: "'Playfair Display', Georgia, serif" }}>
            Your request is in! 🍷🎄
          </h1>
          <p className="text-gray-600">Thank you! A member of our team will <strong>call you shortly</strong> to finalize your gift and arrange payment by phone or in store.</p>
          <div className="mt-5"><Deadline /></div>
          <Link to="/wine-of-the-year" className="inline-block mt-6 text-sm font-semibold underline" style={{ color: WINE }}>
            Back to Wine of the Year
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#fdfbf5' }} data-testid="holiday-gifting-page">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: '#4A2E1E' }}>
        <img src={IMG.hero} alt="Custom holiday wine bottles" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(100deg, rgba(58,16,20,0.92) 30%, rgba(58,16,20,0.3) 100%)' }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-[#F5EBDC]">
          <p className="inline-flex items-center gap-2 text-xs tracking-[3px] font-semibold" style={{ color: GOLD }}>
            <Sparkles className="w-4 h-4" /> THE LOKAL WINERY · HOLIDAY GIFTING
          </p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-2xl" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Give a gift they'll remember — custom wine for the holidays
          </h1>
          <p className="mt-5 max-w-xl text-base sm:text-lg opacity-95">
            Put a favourite photo on a custom holiday wine label — red or white, a thoughtful one-of-a-kind
            present ready to unwrap. Perfect for Christmas & New Year, for loved ones and clients alike.
          </p>
          <div className="mt-7"><Deadline /></div>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button onClick={scrollToForm} data-testid="hero-request-cta"
              className="text-base font-semibold px-7 py-6 rounded-full" style={{ background: GOLD, color: '#4A2E1E' }}>
              <Gift className="w-5 h-5 mr-2" /> Request your gift
            </Button>
          </div>
        </div>
      </section>

      {/* Offering */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-sm">
            <img src={IMG.bottle} alt="Personalized custom wine label bottle" className="w-full h-full object-cover" />
          </div>
          <div data-testid="offering-bottles">
            <div className="flex items-center gap-2" style={{ color: WINE }}>
              <Wine className="w-5 h-5" />
              <h2 className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Personalized Gift Bottles</h2>
            </div>
            <p className="mt-4 text-gray-600 text-base sm:text-lg">
              Put a favourite photo on a custom holiday wine label — a one-of-a-kind present ready to unwrap.
              Choose a red or white wine, and we'll handle the rest.
            </p>
            <ul className="mt-5 space-y-2 text-gray-700">
              {['Your photo on a 3″ × 4″ custom label', 'Red or white wine · 15 or 30 bottles', 'Perfect for Christmas, New Year & clients'].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: WINE }} /> {t}</li>
              ))}
            </ul>
            <Button onClick={scrollToForm} className="mt-7 rounded-full px-6 py-5 text-base font-semibold" style={{ background: WINE, color: '#fff' }} data-testid="offering-cta-bottles">
              <Gift className="w-5 h-5 mr-2" /> Request your gift
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="grid grid-cols-3 gap-3">
          {[IMG.bottle, IMG.hero, IMG.business].map((src, i) => (
            <div key={i} className="aspect-square rounded-xl overflow-hidden">
              <img src={src} alt="Custom holiday wine gift" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-gray-400 mt-3">Sample visuals — ask us about label designs and gift presentation.</p>
      </section>

      {/* Business note */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
        <h3 className="text-2xl font-bold" style={{ color: WINE, fontFamily: "'Playfair Display', Georgia, serif" }}>Gifting for your business?</h3>
        <p className="mt-2 text-gray-600">Branded custom bottles make a memorable gift for clients and staff. Choose 15 or 30 bottles in the form and we'll take care of the rest.</p>
      </section>

      {/* Request form */}
      <section id="gift-form" className="py-14 sm:py-20" style={{ background: '#fff' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold" style={{ color: WINE, fontFamily: "'Playfair Display', Georgia, serif" }}>Request your holiday gift</h2>
            <p className="mt-2 text-gray-600">No payment online — we'll call you to finalize details and arrange payment by phone or in store.</p>
            <div className="mt-4 flex justify-center"><Deadline /></div>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5 bg-[#fdfbf5] rounded-2xl p-6 border border-[#f0e4c8]">
            <div>
              <Label className="mb-2 block text-sm font-semibold" style={{ color: WINE }}>This gift is…</Label>
              <div className="flex gap-2">
                <Chip active={form.giftType === 'personal'} onClick={() => set('giftType', 'personal')} testId="gift-type-personal">Personal</Chip>
                <Chip active={form.giftType === 'business'} onClick={() => set('giftType', 'business')} testId="gift-type-business">Business</Chip>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs text-gray-600">Your name *</Label><Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="gift-name" /></div>
              <div><Label className="text-xs text-gray-600">Phone *</Label><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="gift-phone" /></div>
              <div><Label className="text-xs text-gray-600">Email</Label><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="gift-email" /></div>
              <div>
                <Label className="text-xs text-gray-600">How many bottles?</Label>
                <div className="mt-1 flex gap-2">
                  <Chip active={form.quantity === '15'} onClick={() => set('quantity', '15')} testId="gift-qty-15">15 bottles</Chip>
                  <Chip active={form.quantity === '30'} onClick={() => set('quantity', '30')} testId="gift-qty-30">30 bottles</Chip>
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs text-gray-600">Occasion</Label><Input value={form.occasion} onChange={(e) => set('occasion', e.target.value)} placeholder="Christmas, New Year, thank-you…" data-testid="gift-occasion" /></div>
              <div>
                <Label className="text-xs text-gray-600">Best way to reach you</Label>
                <select value={form.preferredContact} onChange={(e) => set('preferredContact', e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm bg-white" data-testid="gift-contact-pref">
                  <option>Phone</option><option>Email</option><option>Text</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-600">Personalization notes (name / message / label idea)</Label>
              <Textarea rows={3} value={form.personalizationNotes} onChange={(e) => set('personalizationNotes', e.target.value)}
                placeholder="e.g. 'Merry Christmas, Mom & Dad' with a snowy cabin label" data-testid="gift-notes" />
            </div>

            {/* Custom label — two simple options */}
            <div className="rounded-xl border border-[#f0e4c8] bg-white p-4" data-testid="label-options">
              <h3 className="text-sm font-bold" style={{ color: WINE }}>Your custom label</h3>
              <p className="mt-1 text-xs text-gray-500">Choose how you'd like to create your labels.</p>

              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <button type="button" onClick={() => setLabel((l) => ({ ...l, option: 'upload' }))} data-testid="label-option-upload"
                  className="text-left rounded-xl border p-4 transition-colors"
                  style={label.option === 'upload' ? { borderColor: WINE, background: '#fbf3f4' } : { borderColor: '#e7dcc4', background: '#fff' }}>
                  <div className="flex items-center gap-2 font-semibold" style={{ color: WINE }}>
                    <Upload className="w-4 h-4" /> Upload your design
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Already have a label or photo ready? Send it over and we'll print it.</p>
                </button>

                <button type="button" onClick={() => { clearPhoto(); setLabel((l) => ({ ...l, option: 'designer' })); }} data-testid="label-option-designer"
                  className="text-left rounded-xl border p-4 transition-colors"
                  style={label.option === 'designer' ? { borderColor: WINE, background: '#fbf3f4' } : { borderColor: '#e7dcc4', background: '#fff' }}>
                  <div className="flex items-center gap-2 font-semibold" style={{ color: WINE }}>
                    <PenTool className="w-4 h-4" /> Work with our designer
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Our designer will create your custom labels with you — no files needed.</p>
                </button>
              </div>

              {label.option === 'upload' && (
                <div className="mt-4" data-testid="label-upload-area">
                  <Label className="text-xs text-gray-600">Your design or photo <span className="text-gray-400">(JPG, PNG or WebP · optional, you can also send it later)</span></Label>
                  <div className="mt-1 flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50" style={{ color: WINE }} data-testid="label-photo-label">
                      <Upload className="w-4 h-4" /> {label.photo ? 'Change file' : 'Upload file'}
                      <input type="file" accept="image/*" className="hidden" onChange={onLabelPhoto} data-testid="label-photo-input" />
                    </label>
                    {label.photo && (
                      <button type="button" onClick={clearPhoto} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700" data-testid="label-photo-clear">
                        <RotateCcw className="w-3.5 h-3.5" /> Remove
                      </button>
                    )}
                  </div>
                  {label.previewUrl && (
                    <div className="mt-3 inline-block rounded-lg border border-[#e7dcc4] p-1 bg-white">
                      <img src={label.previewUrl} alt="Your uploaded design" className="h-28 w-auto rounded object-contain" data-testid="label-photo-preview" />
                    </div>
                  )}
                </div>
              )}
            </div>

            <Button type="submit" disabled={submitting} className="w-full text-base font-semibold py-6 rounded-full" style={{ background: WINE, color: '#fff' }} data-testid="gift-submit">
              {submitting ? 'Sending…' : <><Phone className="w-5 h-5 mr-2" /> Request my gift — we'll call to finalize</>}
            </Button>
            <p className="text-xs text-gray-400 text-center">By submitting, you agree we may contact you about this request. No payment is taken online.</p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default HolidayGifting;
